/**
 * DNA Growth AI - Analytics e Projeções
 * Fase 7 do DNA Growth Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, Users, DollarSign, Target, BarChart3, 
  LineChart, ArrowUp, ArrowDown, Sparkles, RefreshCw,
  Loader2, Calendar, PieChart, Activity, Zap
} from 'lucide-react';

const API_URL = window.location.origin || '';

export default function DnaGrowthAI() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [projecoes, setProjecoes] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [analiseIA, setAnaliseIA] = useState(null);
  const [loadingAnalise, setLoadingAnalise] = useState(false);
  
  // Config projeção
  const [cenario, setCenario] = useState('moderado');
  const [mesesProjecao, setMesesProjecao] = useState(12);
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };
  
  const carregarDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/growth/analytics/dashboard`, { headers: getAuthHeaders() });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const carregarKPIs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/analytics/kpis`, { headers: getAuthHeaders() });
      const data = await res.json();
      setKpis(data);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  const carregarProjecoes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/analytics/projecoes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ cenario, meses_projecao: mesesProjecao, considerar_sazonalidade: true })
      });
      const data = await res.json();
      setProjecoes(data);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, [cenario, mesesProjecao]);
  
  const carregarComparativo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/analytics/projecoes/comparativo`, { headers: getAuthHeaders() });
      const data = await res.json();
      setComparativo(data);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  const gerarAnaliseIA = async () => {
    setLoadingAnalise(true);
    try {
      const res = await fetch(`${API_URL}/api/growth/analytics/analise-ia`, { headers: getAuthHeaders() });
      const data = await res.json();
      setAnaliseIA(data);
    } catch (error) {
      toast.error('Erro ao gerar análise');
    } finally {
      setLoadingAnalise(false);
    }
  };
  
  useEffect(() => {
    carregarDashboard();
    carregarKPIs();
  }, [carregarDashboard, carregarKPIs]);
  
  useEffect(() => {
    if (activeTab === 'projecoes') {
      carregarProjecoes();
      carregarComparativo();
    }
  }, [activeTab, carregarProjecoes, carregarComparativo]);
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };
  
  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-blue-500" />
            DNA Growth AI
          </h1>
          <p className="text-gray-500 mt-1">Analytics e Projeções de Crescimento</p>
        </div>
        
        <Button 
          onClick={gerarAnaliseIA} 
          disabled={loadingAnalise}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
        >
          {loadingAnalise ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Análise IA
        </Button>
      </div>
      
      {/* Análise IA */}
      {analiseIA && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Análise de Crescimento (IA)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-gray-700">{analiseIA.analise}</div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="kpis">KPIs</TabsTrigger>
          <TabsTrigger value="projecoes">Projeções</TabsTrigger>
        </TabsList>
        
        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            </div>
          ) : dashboard && (
            <>
              {/* Métricas Principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total Alunos</p>
                        <p className="text-3xl font-bold">{dashboard.resumo?.total_alunos || 0}</p>
                      </div>
                      <Users className="w-10 h-10 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Receita Mensal</p>
                        <p className="text-2xl font-bold">{formatCurrency(dashboard.resumo?.receita_mensal_estimada || 0)}</p>
                      </div>
                      <DollarSign className="w-10 h-10 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Taxa Conversão</p>
                        <p className="text-3xl font-bold">{dashboard.resumo?.taxa_conversao || 0}%</p>
                      </div>
                      <Target className="w-10 h-10 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">NPS</p>
                        <p className="text-3xl font-bold">{dashboard.resumo?.nps || 0}</p>
                      </div>
                      <Activity className="w-10 h-10 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Métricas Secundárias */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Leads do Mês</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboard.resumo?.leads_mes || 0}</p>
                      </div>
                      <div className={`flex items-center gap-1 ${dashboard.variacoes?.leads >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {dashboard.variacoes?.leads >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                        <span className="font-medium">{Math.abs(dashboard.variacoes?.leads || 0)}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Churn Rate</p>
                        <p className="text-2xl font-bold text-gray-800">{dashboard.resumo?.churn_rate || 0}%</p>
                      </div>
                      <Badge variant={dashboard.resumo?.churn_rate < 3 ? 'default' : 'destructive'}>
                        {dashboard.resumo?.churn_rate < 3 ? 'Saudável' : 'Atenção'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Ticket Médio</p>
                        <p className="text-2xl font-bold text-gray-800">{formatCurrency(dashboard.resumo?.ticket_medio || 0)}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-gray-300" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Tendências */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="w-5 h-5" />
                    Tendências (Últimos 6 Meses)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Período</th>
                          <th className="text-right py-2">Matrículas</th>
                          <th className="text-right py-2">Leads</th>
                          <th className="text-right py-2">Conversões</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.tendencias?.map((t, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 font-medium">{t.mes_nome}</td>
                            <td className="text-right py-2">{t.matriculas}</td>
                            <td className="text-right py-2">{t.leads}</td>
                            <td className="text-right py-2">{t.conversoes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              
              {/* Segmentação */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5" />
                    Alunos por Segmento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(dashboard.segmentos || {}).map(([key, value]) => {
                      const labels = {
                        educacao_infantil: 'Ed. Infantil',
                        fundamental_i: 'Fundamental I',
                        fundamental_ii: 'Fundamental II',
                        ensino_medio: 'Ensino Médio'
                      };
                      const colors = {
                        educacao_infantil: 'from-pink-500 to-pink-600',
                        fundamental_i: 'from-blue-500 to-blue-600',
                        fundamental_ii: 'from-green-500 to-green-600',
                        ensino_medio: 'from-purple-500 to-purple-600'
                      };
                      
                      return (
                        <div key={key} className={`p-4 rounded-xl bg-gradient-to-br ${colors[key]} text-white`}>
                          <p className="text-sm opacity-80">{labels[key]}</p>
                          <p className="text-2xl font-bold">{value}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        
        {/* KPIs */}
        <TabsContent value="kpis" className="space-y-6">
          {kpis && (
            <>
              <div className="grid md:grid-cols-3 gap-6">
                {Object.entries(kpis.kpis || {}).map(([key, kpi]) => (
                  <Card key={key} className={kpi.status === 'atencao' ? 'border-yellow-300' : ''}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <p className="text-sm text-gray-500">{kpi.descricao}</p>
                        {kpi.status && (
                          <Badge variant={kpi.status === 'saudavel' || kpi.status === 'excelente' || kpi.status === 'bom' ? 'default' : 'destructive'}>
                            {kpi.status}
                          </Badge>
                        )}
                      </div>
                      <p className="text-3xl font-bold text-gray-800">
                        {typeof kpi.valor === 'number' && kpi.valor > 100 
                          ? formatCurrency(kpi.valor) 
                          : kpi.valor}
                      </p>
                      {kpi.formula && (
                        <p className="text-xs text-gray-400 mt-2">{kpi.formula}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <Card>
                <CardHeader>
                  <CardTitle>Referências</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {Object.entries(kpis.referencias || {}).map(([key, valor]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-600">{key.replace(/_/g, ' ')}</span>
                        <Badge variant="outline">{valor}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        
        {/* PROJEÇÕES */}
        <TabsContent value="projecoes" className="space-y-6">
          {/* Controles */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div>
                  <label className="text-sm text-gray-500 block mb-1">Cenário</label>
                  <Select value={cenario} onValueChange={setCenario}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservador">Conservador</SelectItem>
                      <SelectItem value="moderado">Moderado</SelectItem>
                      <SelectItem value="otimista">Otimista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm text-gray-500 block mb-1">Período</label>
                  <Select value={mesesProjecao.toString()} onValueChange={(v) => setMesesProjecao(parseInt(v))}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 meses</SelectItem>
                      <SelectItem value="12">12 meses</SelectItem>
                      <SelectItem value="24">24 meses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button onClick={carregarProjecoes} variant="outline" className="mt-5">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Recalcular
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Comparativo de Cenários */}
          {comparativo && (
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(comparativo.comparativo || {}).map(([key, dados]) => {
                const colors = {
                  conservador: 'from-gray-500 to-gray-600',
                  moderado: 'from-blue-500 to-blue-600',
                  otimista: 'from-green-500 to-green-600'
                };
                const labels = {
                  conservador: 'Conservador',
                  moderado: 'Moderado',
                  otimista: 'Otimista'
                };
                
                return (
                  <Card 
                    key={key} 
                    className={`overflow-hidden ${cenario === key ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setCenario(key)}
                  >
                    <div className={`h-2 bg-gradient-to-r ${colors[key]}`} />
                    <CardContent className="p-4">
                      <p className="font-medium text-gray-600 mb-3">{labels[key]}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Alunos</span>
                          <span className="font-bold">{dados.alunos_final}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Receita</span>
                          <span className="font-bold">{formatCurrency(dados.receita_final)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Crescimento</span>
                          <span className={`font-bold ${dados.crescimento >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {dados.crescimento >= 0 ? '+' : ''}{dados.crescimento}%
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          
          {/* Detalhes da Projeção */}
          {projecoes && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Projeção Mensal - Cenário {cenario.charAt(0).toUpperCase() + cenario.slice(1)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Mês</th>
                        <th className="text-right py-2">Alunos</th>
                        <th className="text-right py-2">Novos</th>
                        <th className="text-right py-2">Cancelamentos</th>
                        <th className="text-right py-2">Receita</th>
                        <th className="text-right py-2">Cresc. Acum.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projecoes.projecoes?.map((p, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="py-2 font-medium">{p.mes_nome}</td>
                          <td className="text-right py-2">{p.alunos}</td>
                          <td className="text-right py-2 text-green-600">+{p.novos}</td>
                          <td className="text-right py-2 text-red-600">-{p.cancelamentos}</td>
                          <td className="text-right py-2">{formatCurrency(p.receita)}</td>
                          <td className="text-right py-2">
                            <Badge variant={p.crescimento_acumulado >= 0 ? 'default' : 'destructive'}>
                              {p.crescimento_acumulado >= 0 ? '+' : ''}{p.crescimento_acumulado}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
